import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FiEdit2 } from 'react-icons/fi';
import { saveHostNotes } from '../../store/thunks/HostActions';

/**
 * Internal (admin-only) notes for a host. Read-only until Edit is pressed.
 */
export default function HostNotesCard({ host, setSuccessMessage, setErrorMessage }) {
  const dispatch = useDispatch();
  const hid = host?._id;
  const [notes, setNotes] = useState(host?.internalHostNotes || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNotes(host?.internalHostNotes || '');
    setEditing(false);
  }, [host]);

  const handleCancel = () => {
    setNotes(host?.internalHostNotes || '');
    setEditing(false);
  };

  const handleSave = async () => {
    if (!hid) return;
    setSaving(true);
    try {
      const action = await dispatch(saveHostNotes({ hid, notes: notes.trim() }));
      if (saveHostNotes.fulfilled.match(action)) {
        setSuccessMessage?.('Internal notes saved.');
        setEditing(false);
      } else {
        setErrorMessage?.(action.payload?.error || action.payload?.message || 'Failed to save notes.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Internal notes</h3>
          <p className="text-xs text-zinc-500">Visible to admins only.</p>
        </div>
        {editing ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="h-8 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="h-8 rounded-lg bg-zinc-900 px-3 text-xs font-semibold text-white transition hover:bg-zinc-800 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition hover:bg-zinc-50"
          >
            <FiEdit2 className="h-3.5 w-3.5" />
            Edit
          </button>
        )}
      </div>

      <textarea
        readOnly={!editing}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        placeholder="Write internal notes for this host…"
        rows={5}
        className={`mt-3 w-full resize-y rounded-xl border px-3 py-2 text-sm text-zinc-900 outline-none transition ${
          editing
            ? 'border-indigo-300 bg-white ring-2 ring-indigo-100'
            : 'border-zinc-200 bg-zinc-50'
        }`}
      />
    </section>
  );
}
